import { call, put, takeEvery, select } from 'redux-saga/effects';
import * as types from './siteProfileActionType';
import * as actions from './siteProfileActions';
import * as services from './siteProfileServices';
import SweetAlert from '../../../../components/sweetalert/SweetAlert';
import { convertTimeWithSecondsTo12Hour } from '../../../../utils/timeFormatUtils';

// Fetch Site Profile
function* fetchSiteProfileSaga(action) {
    try {
        const response = yield call(services.getSiteProfileData, action.payload);

        if (response?.success) {
            yield put(actions.fetchSiteProfileSuccess(response));
        } else {
            yield put(
                actions.fetchSiteProfileFailure(
                    response?.errorMessage || 'Failed to fetch facility profile'
                )
            );
        }
    } catch (error) {
        const errorMessage = 'Failed to load Facility Profile';
        yield put(actions.fetchSiteProfileFailure(errorMessage));
        SweetAlert.error({ title: 'Error!', text: errorMessage });
    }
}

// Fetch Site Coverage Hours
function* fetchSiteCoverageHoursSaga(action) {
    try {
        const response = yield call(
            services.getSiteCoverageHoursData,
            action.payload
        );

        if (response?.success) {
            yield put(actions.fetchSiteCoverageHoursSuccess(response));
        } else {
            yield put(
                actions.fetchSiteCoverageHoursFailure(
                    response?.errorMessage ||
                    'Failed to fetch facility coverage hours'
                )
            );
        }
    } catch (error) {
        const errorMessage = 'Failed to load Facility Coverage Hours';
        yield put(actions.fetchSiteCoverageHoursFailure(errorMessage));
        SweetAlert.error({ title: 'Error!', text: errorMessage });
    }
}

// Fetch Site Associated Radiologists
function* fetchSiteAssociatedRadsSaga(action) {
    try {
        const response = yield call(
            services.getSiteAssociatedRadsData,
            action.payload
        );

        if (response?.success) {
            yield put(
                actions.fetchSiteAssociatedRadsSuccess(response.values || {})
            );
        } else {
            yield put(
                actions.fetchSiteAssociatedRadsFailure(
                    response?.errorMessage ||
                    'Failed to fetch facility associated providers'
                )
            );
        }
    } catch (error) {
        const errorMessage = 'Failed to load Facility Associated Providers';
        yield put(actions.fetchSiteAssociatedRadsFailure(errorMessage));
        SweetAlert.error({ title: 'Error!', text: errorMessage });
    }
}

// Fetch Site Calendar Demand
function* fetchSiteCalendarDemandSaga(action) {
    try {
        const { siteId, demandDate } = action.payload;
        const response = yield call(
            services.getSiteCalendarDemandData,
            siteId,
            demandDate
        );

        if (response?.success) {
            yield put(actions.fetchSiteCalendarDemandSuccess(response));
        } else {
            yield put(
                actions.fetchSiteCalendarDemandFailure(
                    response?.errorMessage ||
                    'Failed to fetch facility calendar demand'
                )
            );
        }
    } catch (error) {
        const errorMessage = 'Failed to load Facility Calendar Demand';
        yield put(actions.fetchSiteCalendarDemandFailure(errorMessage));
        SweetAlert.error({ title: 'Error!', text: errorMessage });
    }
}

// Fetch Site Calendar Demand By Date
function* fetchSiteCalendarDemandByDateSaga(action) {
    try {
        const { siteId, demandDate } = action.payload;
        const response = yield call(
            services.getSiteCalendarDemandByDateData,
            siteId,
            demandDate
        );

        if (response?.success) {
            yield put(actions.fetchSiteCalendarDemandByDateSuccess(response));
        } else {
            yield put(
                actions.fetchSiteCalendarDemandByDateFailure(
                    response?.errorMessage ||
                    'Failed to fetch facility calendar demand by date'
                )
            );
        }
    } catch (error) {
        const errorMessage = 'Failed to load Facility Calendar Demand By Date';
        yield put(
            actions.fetchSiteCalendarDemandByDateFailure(errorMessage)
        );
        SweetAlert.error({ title: 'Error!', text: errorMessage });
    }
}

// Fetch Site RADS Demand Schedule
function* fetchSiteRadsDemandScheduleSaga(action) {
    try {
        const { siteId, demandDate, demandTime } = action.payload;
        const response = yield call(
            services.getSiteRadsDemandScheduleData,
            siteId,
            demandDate,
            demandTime
        );

        if (response?.success) {
            // Format StartHour and EndHour to 12-hour format
            const formattedValues = response.values?.map(item => {
                const startHour = convertTimeWithSecondsTo12Hour(item.StartHour);
                const endHour = convertTimeWithSecondsTo12Hour(item.EndHour);
                
                return {
                    ...item,
                    StartHour: startHour,
                    EndHour: endHour
                };
            });

            const formattedResponse = {
                ...response,
                values: formattedValues
            };

            yield put(actions.fetchSiteRadsDemandScheduleSuccess(formattedResponse));
        } else {
            yield put(
                actions.fetchSiteRadsDemandScheduleFailure(
                    response?.errorMessage ||
                    'Failed to fetch facility provider demand schedule'
                )
            );
        }
    } catch (error) {
        const errorMessage = 'Failed to load Facility Provider Demand Schedule';
        yield put(actions.fetchSiteRadsDemandScheduleFailure(errorMessage));
        SweetAlert.error({ title: 'Error!', text: errorMessage });
    }
}

// Fetch Site Associated RAD Metrics Saga
function* fetchSiteAssociatedRadMetricsSaga(action) {
    try {
        const response = yield call(services.getSiteAssociatedRadMetricsData, action.payload);

        if (response?.success) {
            yield put(actions.fetchSiteAssociatedRadMetricsSuccess(response));
        } else {
            yield put(actions.fetchSiteAssociatedRadMetricsFailure(response?.errorMessage || 'Failed to fetch facility associated provider metrics'));
        }
    } catch (error) {
        console.error('Site Associated RAD Metrics Fetch Error:', error);
        yield put(actions.fetchSiteAssociatedRadMetricsFailure('Failed to fetch facility associated provider metrics'));
    }
}

// Fetch Site RAD Metrics Dropdown Saga
function* fetchSiteRadMetricsDropdownSaga(action) {
    try {
        const response = yield call(services.getSiteRadMetricsDropdownData, action.payload);

        if (response?.success) {
            yield put(actions.fetchSiteRadMetricsDropdownSuccess(response.values || {}));
        } else {
            yield put(actions.fetchSiteRadMetricsDropdownFailure(response?.errorMessage || 'Failed to fetch dropdown data'));
        }
    } catch (error) {
        console.error('Site RAD Metrics Dropdown Fetch Error:', error);
        yield put(actions.fetchSiteRadMetricsDropdownFailure('Failed to fetch dropdown data'));
    }
}
 
// Fetch Site Encore Child Tables Saga
function* fetchSiteMatrixChildTablesSaga(action) {
    try {
        const response = yield call(services.getSiteMatrixChildTablesData, action.payload);

        if (response?.success) {
            yield put(actions.fetchSiteMatrixChildTablesSuccess(response));
        } else {
            yield put(actions.fetchSiteMatrixChildTablesFailure(response?.errorMessage || 'Failed to fetch facility matrix child tables'));
        }
    } catch (error) {
        console.error('Facility Encore Child Tables Fetch Error:', error);
        yield put(actions.fetchSiteMatrixChildTablesFailure('Failed to fetch facility encore child tables'));
    }
}

// Export Site Associated RAD Metrics
function* exportSiteAssociatedRadMetricsSaga(action) {
    try {
        const response = yield call(
            services.exportSiteAssociatedRadMetricsData,
            action.payload
        );

        if (response?.success) {
            const { downloadBase64File } = yield import(
                '../../../../utils/fileDownloads'
            );

            const base64Data = response?.values || response;
            const state = yield select();
            const siteName =
                state.site?.siteProfile?.values?.SiteName || 'Facility';

            yield call(
                downloadBase64File,
                base64Data,
                `${siteName}_ProviderMetrics`
            );

            yield put(actions.exportSiteAssociatedRadMetricsSuccess(response));
            SweetAlert.success({
                title: 'Success!',
                text: 'Facility provider metrics exported successfully'
            });
        } else {
            const errorMessage =
                response?.errorMessage || 'Failed to export facility provider metrics';

            yield put(
                actions.exportSiteAssociatedRadMetricsFailure(errorMessage)
            );
            SweetAlert.error({
                title: 'Error!',
                text: errorMessage
            });
        }
    } catch (error) {
        const errorMessage = 'Failed to export Facility Provider Metrics';

        yield put(actions.exportSiteAssociatedRadMetricsFailure(errorMessage));
        SweetAlert.error({
            title: 'Error!',
            text: errorMessage
        });
    }
}

// Fetch Site Profile Demand By Hours Chart Saga
function* fetchSiteProfileDemandByHoursChartSaga(action) {
    try {
        const response = yield call(services.getSiteProfileDemandByHoursChartData, action.payload);

        if (response?.success) {
            yield put(actions.fetchSiteProfileDemandByHoursChartSuccess(response));
        } else {
            yield put(actions.fetchSiteProfileDemandByHoursChartFailure(response?.errorMessage || 'Failed to fetch facility profile demand by hours chart'));
        }
    } catch (error) {
        console.error('Site Profile Demand By Hours Chart Fetch Error:', error);
        yield put(actions.fetchSiteProfileDemandByHoursChartFailure('Failed to fetch facility profile demand by hours chart'));
    }
}

// Fetch Site Profile Volume By Priority Chart Saga
function* fetchSiteProfileVolumeByPriorityChartSaga(action) {
    try {
        const response = yield call(services.getSiteProfileVolumeByPriorityChartData, action.payload);

        if (response?.success) {
            yield put(actions.fetchSiteProfileVolumeByPriorityChartSuccess(response));
        } else {
            yield put(actions.fetchSiteProfileVolumeByPriorityChartFailure(response?.errorMessage || 'Failed to fetch facility profile volume by priority chart'));
        }
    } catch (error) {
        console.error('Site Profile Volume By Priority Chart Fetch Error:', error);
        yield put(actions.fetchSiteProfileVolumeByPriorityChartFailure('Failed to fetch facility profile volume by priority chart'));
    }
}

// Fetch Site Profile Volume By Modality Chart Saga
function* fetchSiteProfileVolumeByModalityChartSaga(action) {
    try {
        const response = yield call(services.getSiteProfileVolumeByModalityChartData, action.payload);

        if (response?.success) {
            yield put(actions.fetchSiteProfileVolumeByModalityChartSuccess(response));
        } else {
            yield put(actions.fetchSiteProfileVolumeByModalityChartFailure(response?.errorMessage || 'Failed to fetch facility profile volume by modality chart'));
        }
    } catch (error) {
        console.error('Site Profile Volume By Modality Chart Fetch Error:', error);
        yield put(actions.fetchSiteProfileVolumeByModalityChartFailure('Failed to fetch facility profile volume by modality chart'));
    }
}


// Fetch Site Profile Metrics Dropdown Saga
function* fetchSiteProfileMetricsDropdownSaga(action) {
    try {
        const response = yield call(services.getSiteProfileMetricsDropdownData);

        if (response?.success) {
            yield put(actions.fetchSiteProfileMetricsDropdownSuccess(response.values || {}));
        } else {
            yield put(actions.fetchSiteProfileMetricsDropdownFailure(response?.errorMessage || 'Failed to fetch facility profile metrics dropdown'));
        }
    } catch (error) {
        console.error('Site Profile Metrics Dropdown Fetch Error:', error);
        yield put(actions.fetchSiteProfileMetricsDropdownFailure('Failed to fetch facility profile metrics dropdown'));
    }
}

// Fetch Site Profile Demand By Hours Chart Details Saga
function* fetchSiteProfileDemandByHoursChartDetailsSaga(action) {
    try {
        const response = yield call(services.getSiteProfileDemandByHoursChartDetailsData, action.payload);

        if (response?.success) {
            yield put(actions.fetchSiteProfileDemandByHoursChartDetailsSuccess(response));
        } else {
            yield put(actions.fetchSiteProfileDemandByHoursChartDetailsFailure(response?.errorMessage || 'Failed to fetch facility profile demand by hours chart'));
        }
    } catch (error) {
        console.error('Site Profile Demand By Hours Chart Details Fetch Error:', error);
        yield put(actions.fetchSiteProfileDemandByHoursChartDetailsFailure('Failed to fetch facility profile demand by hours chart'));
    }
}

// Fetch Site Profile Volume By Modality Chart Details Saga
function* fetchSiteProfileVolumeByModalityChartDetailsSaga(action) {
    try {
        const response = yield call(services.getSiteProfileVolumeByModalityChartDetailsData, action.payload);

        if (response?.success) {
            yield put(actions.fetchSiteProfileVolumeByModalityChartDetailsSuccess(response));
        } else {
            yield put(actions.fetchSiteProfileVolumeByModalityChartDetailsFailure(response?.errorMessage || 'Failed to fetch facility profile volume by modality chart'));
        }
    } catch (error) {
        console.error('Site Profile Volume By Modality Chart Details Fetch Error:', error);
        yield put(actions.fetchSiteProfileVolumeByModalityChartDetailsFailure('Failed to fetch facility profile volume by modality chart'));
    }
}

// Fetch Site Profile Volume By Priority Chart Details Saga
function* fetchSiteProfileVolumeByPriorityChartDetailsSaga(action) {
    try {
        const response = yield call(services.getSiteProfileVolumeByPriorityChartDetailsData, action.payload);

        if (response?.success) {
            yield put(actions.fetchSiteProfileVolumeByPriorityChartDetailsSuccess(response));
        } else {
            yield put(actions.fetchSiteProfileVolumeByPriorityChartDetailsFailure(response?.errorMessage || 'Failed to fetch facility profile volume by priority chart'));
        }
    } catch (error) {
        console.error('Site Profile Volume By Priority Chart Details Fetch Error:', error);
        yield put(actions.fetchSiteProfileVolumeByPriorityChartDetailsFailure('Failed to fetch facility profile volume by priority chart'));
    }
}

// Associate RAD to Site
function* associateRadToSiteSaga(action) {
    try {
        const { siteId, ...payload } = action.payload || {};
        const response = yield call(services.associateRadToSite, payload);

        if (response?.success) {
            yield put(actions.associateRadToSiteSuccess(response));
            SweetAlert.success({
                title: 'Success!',
                text: response.resultMessage || 'Provider associated with facility.'
            });
            if (siteId) {
                yield put(actions.fetchSiteAssociatedRadsRequest(siteId));
            }
        } else {
            const errorMessage = response?.errorMessage || 'Failed to associate provider with facility.';
            yield put(actions.associateRadToSiteFailure(errorMessage));
            SweetAlert.error({ title: 'Error!', text: errorMessage });
        }
    } catch (error) {
        const errorMessage = 'Failed to associate provider with facility.';
        yield put(actions.associateRadToSiteFailure(errorMessage));
        SweetAlert.error({ title: 'Error!', text: errorMessage });
    }
}

// Disassociate RAD from Site
function* disassociateRadFromSiteSaga(action) {
    try {
        const { siteId, ...payload } = action.payload || {};
        const response = yield call(services.disassociateRadFromSite, payload);

        if (response?.success) {
            yield put(actions.disassociateRadFromSiteSuccess(response));
            SweetAlert.success({
                title: 'Success!',
                text: response.resultMessage || 'Provider removed from facility.'
            });
            if (siteId) {
                yield put(actions.fetchSiteAssociatedRadsRequest(siteId));
            }
        } else {
            const errorMessage = response?.errorMessage || 'Failed to remove provider from facility.';
            yield put(actions.disassociateRadFromSiteFailure(errorMessage));
            SweetAlert.error({ title: 'Error!', text: errorMessage });
        }
    } catch (error) {
        const errorMessage = 'Failed to remove provider from facility.';
        yield put(actions.disassociateRadFromSiteFailure(errorMessage));
        SweetAlert.error({ title: 'Error!', text: errorMessage });
    }
}

// Fetch RADs not associated to Site
function* fetchRadsNotAssociatedToSiteSaga(action) {
    try {
        const response = yield call(services.getRadsNotAssociatedToSite, action.payload);
        if (response?.success) {
            yield put(actions.fetchRadsNotAssociatedToSiteSuccess(response.values || []));
        } else {
            yield put(actions.fetchRadsNotAssociatedToSiteFailure(response?.errorMessage || 'Failed to load providers.'));
        }
    } catch (error) {
        yield put(actions.fetchRadsNotAssociatedToSiteFailure('Failed to load providers.'));
    }
}

// -------------------- FETCH SITE RAD NOTES --------------------
function* fetchSiteRadNotesSaga(action) {
    try {
        const response = yield call(services.getSiteRadNotes, action.payload);
        if (response?.success) {
            const notes = response?.value || response?.Value || response?.values || response?.Values || [];
            yield put(actions.fetchSiteRadNotesSuccess(Array.isArray(notes) ? notes : []));
        } else {
            yield put(actions.fetchSiteRadNotesFailure('No notes found'));
        }
    } catch (error) {
        yield put(actions.fetchSiteRadNotesFailure('Failed to fetch notes'));
    }
}

function* fetchSiteRadAllNotesSaga(action) {
    try {
        const response = yield call(services.getSiteLicenseAndRadSiteAssignmentNotes, action.payload);
        if (response?.success) {
            const notes = response?.value || response?.Value || response?.values || response?.Values || [];
            yield put(actions.fetchSiteRadAllNotesSuccess(Array.isArray(notes) ? notes : []));
        } else {
            yield put(actions.fetchSiteRadAllNotesFailure('No notes found'));
        }
    } catch (error) {
        yield put(actions.fetchSiteRadAllNotesFailure('Failed to fetch notes'));
    }
}

// -------------------- ADD SITE RAD NOTE --------------------
function* addSiteRadNoteSaga(action) {
    try {
        const response = yield call(services.addSiteRadNote, action.payload);
        if (response?.success) {
            yield put(actions.addSiteRadNoteSuccess());
            yield put(actions.fetchSiteRadNotesRequest({
                radiologistSiteAssignmentId: action.payload.RadiologistSiteAssignmentID,
            }));
            SweetAlert.success({ title: 'Success!', text: response?.resultMessage || 'Note added successfully!' });
        } else {
            yield put(actions.addSiteRadNoteFailure('Failed to save note'));
            SweetAlert.error({ title: 'Error!', text: response?.errorMessage || 'Failed to save note.' });
        }
    } catch (error) {
        yield put(actions.addSiteRadNoteFailure('Failed to save note'));
        SweetAlert.error({ title: 'Error!', text: 'An unexpected error occurred while saving the note.' });
    }
}

// Watchers
export default function* siteProfileSaga() {
    yield takeEvery(types.FETCH_SITE_PROFILE_REQUEST, fetchSiteProfileSaga);
    yield takeEvery(types.FETCH_SITE_COVERAGE_HOURS_REQUEST,fetchSiteCoverageHoursSaga);
    yield takeEvery( types.FETCH_SITE_ASSOCIATED_RADS_REQUEST,fetchSiteAssociatedRadsSaga);
    yield takeEvery(types.FETCH_SITE_CALENDAR_DEMAND_REQUEST,fetchSiteCalendarDemandSaga);
    yield takeEvery(types.FETCH_SITE_CALENDAR_DEMAND_BY_DATE_REQUEST,fetchSiteCalendarDemandByDateSaga);
    yield takeEvery(types.FETCH_SITE_RADS_DEMAND_SCHEDULE_REQUEST,fetchSiteRadsDemandScheduleSaga);
    yield takeEvery(types.EXPORT_SITE_ASSOCIATED_RAD_METRICS_REQUEST,exportSiteAssociatedRadMetricsSaga);
    yield takeEvery(types.FETCH_SITE_ASSOCIATED_RAD_METRICS_REQUEST, fetchSiteAssociatedRadMetricsSaga);
    yield takeEvery(types.FETCH_SITE_MATRIX_CHILD_TABLES_REQUEST, fetchSiteMatrixChildTablesSaga);
    yield takeEvery(types.FETCH_SITE_RAD_METRICS_DROPDOWN_REQUEST, fetchSiteRadMetricsDropdownSaga);
    yield takeEvery(types.FETCH_SITE_PROFILE_DEMAND_BY_HOURS_CHART_REQUEST, fetchSiteProfileDemandByHoursChartSaga);
    yield takeEvery(types.FETCH_SITE_PROFILE_VOLUME_BY_PRIORITY_CHART_REQUEST, fetchSiteProfileVolumeByPriorityChartSaga);
    yield takeEvery(types.FETCH_SITE_PROFILE_VOLUME_BY_MODALITY_CHART_REQUEST, fetchSiteProfileVolumeByModalityChartSaga);
    yield takeEvery(types.FETCH_SITE_PROFILE_METRICS_DROPDOWN_REQUEST, fetchSiteProfileMetricsDropdownSaga);
    yield takeEvery(types.FETCH_SITE_PROFILE_DEMAND_BY_HOURS_CHART_DETAILS_REQUEST, fetchSiteProfileDemandByHoursChartDetailsSaga);
    yield takeEvery(types.FETCH_SITE_PROFILE_VOLUME_BY_MODALITY_CHART_DETAILS_REQUEST, fetchSiteProfileVolumeByModalityChartDetailsSaga);
    yield takeEvery(types.FETCH_SITE_PROFILE_VOLUME_BY_PRIORITY_CHART_DETAILS_REQUEST, fetchSiteProfileVolumeByPriorityChartDetailsSaga);
    yield takeEvery(types.ASSOCIATE_RAD_TO_SITE_REQUEST, associateRadToSiteSaga);
    yield takeEvery(types.DISASSOCIATE_RAD_FROM_SITE_REQUEST, disassociateRadFromSiteSaga);
    yield takeEvery(types.FETCH_RADS_NOT_ASSOCIATED_TO_SITE_REQUEST, fetchRadsNotAssociatedToSiteSaga);
    yield takeEvery(types.FETCH_SITE_RAD_NOTES_REQUEST, fetchSiteRadNotesSaga);
    yield takeEvery(types.FETCH_SITE_RAD_ALL_NOTES_REQUEST, fetchSiteRadAllNotesSaga);
    yield takeEvery(types.ADD_SITE_RAD_NOTE_REQUEST, addSiteRadNoteSaga);
}
